import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "../styles/hero-banner.css";

import PLACEHOLDER from "../../src/assets/images/logo192.png";

const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

const IMAGE_BASE = "https://afrogebeya.com/uploads/products";

const resolveImage = (url) => {
  if (!url) return PLACEHOLDER;

  return `${IMAGE_BASE}/${url.replace(/\\/g, "/")}`;
};

const createSlug = (name = "", id) =>
  `${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")}-${id}`;

export default function HeroBanner() {
  const [itemsPerSlide, setItemsPerSlide] = useState(5);
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  useEffect(() => {
    const fetchHeroProducts = async () => {
      try {
        setLoading(true);

        const response = await axios.get(`${PRODUCT_API}/products/buyers`, {
          params: {
            page: 0,
            size: 25,
          },
        });

        setProducts(response.data?.content || []);
      } catch (error) {
        console.error("Hero products loading failed", error);

        setError("Unable to load featured products");
      } finally {
        setLoading(false);
      }
    };

    fetchHeroProducts();
  }, []);

  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 480) setItemsPerSlide(2);
      else if (window.innerWidth < 992) setItemsPerSlide(2);
      else setItemsPerSlide(5);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const groupedProducts = useMemo(() => {
    const groups = [];
    for (let i = 0; i < products.length; i += itemsPerSlide) {
      groups.push(products.slice(i, i + itemsPerSlide));
    }
    return groups;
  }, [products, itemsPerSlide]);

  if (loading) {
    return (
      <section className="hero-wrapper">
        <div className="hero-loading">
          {Array.from({
            length: itemsPerSlide,
          }).map((_, index) => (
            <div key={index} className="hero-skeleton-card" />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="hero-wrapper">
        <div className="alert alert-warning text-center">{error}</div>
      </section>
    );
  }

  if (groupedProducts.length === 0) {
    return null;
  }

  return (
    <section className="hero-wrapper">
      <div
        id="homeHeroCarousel"
        className="carousel slide home-hero-carousel"
        data-bs-ride="carousel"
        data-bs-interval="5000"
      >
        {/* Indicators */}

        <div className="carousel-indicators">
          {groupedProducts.map((_, index) => (
            <button
              key={index}
              type="button"
              data-bs-target="#homeHeroCarousel"
              data-bs-slide-to={index}
              className={index === 0 ? "active" : ""}
            />
          ))}
        </div>

        {/* Slides */}

        <div className="carousel-inner">
          {groupedProducts.map((slide, index) => (
            <div
              key={index}
              className={`carousel-item ${index === 0 ? "active" : ""}`}
            >
              <div className="home-hero-slide">
                <div className="hero-products-grid">
                  {slide.map((product) => (
                    <div
                      key={product.id}
                      className="hero-product-card"
                      onClick={() => {
                        navigate(
                          `/products/${createSlug(
                            product.productName,
                            product.id,
                          )}`,
                        );
                      }}
                    >
                      <div className="hero-product-image-wrapper">
                        <img
                          src={resolveImage(product.mainImageUrl)}
                          alt={product.productName}
                          className="hero-product-image"
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = PLACEHOLDER;
                          }}
                        />
                      </div>

                      <div className="hero-product-info">
                        <h6>{product.productName}</h6>
                        {product.price !== null &&
                          product.price !== undefined && (
                            <span className="hero-product-price">
                              {new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: product.currency || "ETB",
                              }).format(product.price)}
                            </span>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Controls */}

        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#homeHeroCarousel"
          data-bs-slide="prev"
        >
          <span className="carousel-control-prev-icon" />
        </button>

        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#homeHeroCarousel"
          data-bs-slide="next"
        >
          <span className="carousel-control-next-icon" />
        </button>
      </div>
    </section>
  );
}
